import { ItemSchema, ZodCreateItemSchema, ZodGetItemsParams, ZodUpdateItemSchema } from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Item = ItemSchema;

export const getItem = async (itemId: string): Promise<Item> => {
  const response = await apiClient.get<{ item: Item }>(`/api/item/${itemId}`);
  return response.data.item;
};

/**
 * Get items - returns all items (for backward compatibility)
 * @deprecated Use getItemsPaginated for better performance with large datasets
 */
export const getItems = async (params?: ZodGetItemsParams): Promise<Item[]> => {
  const response = await apiClient.get<{ items: Item[]; pagination: PaginationMeta }>("/api/item", { params });
  return response.data.items;
};

/**
 * Paginated response for items
 */
export interface GetItemsPaginatedResponse {
  items: Item[];
  pagination: PaginationMeta;
}

/**
 * Get items with pagination support
 */
export const getItemsPaginated = async (
  params?: ZodGetItemsParams & PaginationParams,
): Promise<GetItemsPaginatedResponse> => {
  const response = await apiClient.get<GetItemsPaginatedResponse>("/api/item", { params });
  return response.data;
};

export const getActiveItems = async (): Promise<Item[]> => {
  const response = await apiClient.get<{ items: Item[] }>("/api/item/active");
  return response.data.items;
};

export const createItem = async (itemData: ZodCreateItemSchema): Promise<Item> => {
  const response = await apiClient.post<{ item: Item }>("/api/item", itemData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.item;
};

export const updateItem = async (itemData: ZodUpdateItemSchema): Promise<Item> => {
  const response = await apiClient.put<{ item: Item }>(`/api/item/${itemData.id}`, itemData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.item;
};

export const deleteItem = async (itemId: string): Promise<{ message: string; item: Item }> => {
  const response = await apiClient.delete<{ message: string; item: Item }>(`/api/item/${itemId}`);
  return response.data;
};

/**
 * Fetch all items by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllItems = async (params?: ZodGetItemsParams): Promise<Item[]> => {
  const allItems: Item[] = [];
  let cursor: string | null = null;

  do {
    const response = await getItemsPaginated({ ...params, cursor, limit: 100 });
    allItems.push(...response.items);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allItems;
};

export default getItem;
